/**
 * Placing a `details.dropdown` panel against the viewport rather than its
 * ancestors.
 *
 * A `DataTable` wraps its rows in `overflow-x-auto`, and CSS forces the other
 * axis to auto with it, so an absolutely positioned panel is clipped to the
 * row it opens from. Fixed positioning from the trigger's rect escapes that,
 * but then nothing keeps the panel on screen: measured at 375px, a
 * right-aligned 343px panel hung 56px off the left edge, outside the document
 * rather than inside it, so it could not be scrolled to.
 *
 * @module components/ui/useDropdownPlacement
 */

import { type RefObject, useCallback, useEffect } from "react";

export interface DropdownPlacement {
  /** The `details` the panel belongs to. Its `summary` is the anchor. */
  detailsRef?: RefObject<HTMLDetailsElement | null>;
  /** For a panel with no `details`: what it hangs from, and whether it is open. */
  anchorRef?: RefObject<HTMLElement | null>;
  open?: boolean;
  /**
   * Sizes the panel to its anchor. Fixed positioning makes the viewport the
   * containing block, so a `w-full` list would otherwise span the screen.
   */
  matchWidth?: boolean;
  /** The panel being placed. */
  panelRef: RefObject<HTMLElement | null>;
  align?: "start" | "end";
  /** The panel never opens above this many px from the top, to clear a fixed bar. */
  minTop?: number;
}

/** A value that makes an element the containing block for a fixed child. */
function contains(value: string | undefined): boolean {
  return Boolean(value) && value !== "none";
}

/** Whether an element's style makes it the containing block for a fixed child. */
function containsFixed(style: CSSStyleDeclaration): boolean {
  return (
    [
      style.transform,
      style.translate,
      style.scale,
      style.rotate,
      style.filter,
      style.getPropertyValue("backdrop-filter"),
      style.perspective,
    ].some(contains) ||
    /\b(transform|translate|scale|rotate|perspective|filter|backdrop-filter)\b/.test(
      style.willChange,
    ) ||
    // Not `container-type`: it no longer carries layout containment, so a size
    // container leaves fixed children on the viewport.
    /\b(layout|paint|strict|content)\b/.test(style.contain)
  );
}

/**
 * Where a fixed panel's offsets count from. The viewport, unless an ancestor
 * contains it, as a transform does: an open daisyUI modal-box keeps `scale: 1`,
 * which makes it the containing block. Read from that ancestor, never the
 * panel, whose own open animation scales it.
 */
function fixedOrigin(panel: HTMLElement): { top: number; left: number } {
  for (let node = panel.parentElement; node; node = node.parentElement) {
    if (containsFixed(getComputedStyle(node))) {
      const rect = node.getBoundingClientRect();
      return {
        top: rect.top + node.clientTop - node.scrollTop,
        left: rect.left + node.clientLeft - node.scrollLeft,
      };
    }
  }
  return { top: 0, left: 0 };
}

/**
 * Keep a dropdown panel anchored to its trigger and inside the viewport.
 *
 * @param placement - The `details`, the panel, and which edge it aligns to.
 * @returns Place it now, for a caller that opens the panel itself: `toggle` is
 *   queued as a task, so the panel would paint unclamped for a frame first.
 */
export function useDropdownPlacement({
  detailsRef = undefined,
  anchorRef = undefined,
  open = false,
  matchWidth = false,
  panelRef,
  align = "end",
  minTop = 0,
}: DropdownPlacement): () => void {
  const place = useCallback(() => {
    const details = detailsRef ? detailsRef.current : null;
    const panel = panelRef.current;
    // Open first, measure second. Every instance keeps a capture scroll
    // listener for life, so measuring before this check would force one layout
    // per closed panel per scroll frame.
    if (!(detailsRef ? details?.open : open) || !panel) {
      return;
    }
    let anchorElement: Element | null | undefined = null;
    if (details) {
      anchorElement = details.querySelector("summary");
    } else if (anchorRef) {
      anchorElement = anchorRef.current;
    }
    const anchor = anchorElement?.getBoundingClientRect();
    if (!anchor) {
      return;
    }
    // Out of flow before the anchor is measured: in flow, a list can raise a
    // scrollbar that narrows the very field it is about to be sized to. The
    // layout width, not the rect's, which an ancestor still scaling shrinks.
    if (matchWidth) {
      panel.style.position = "fixed";
    }
    const anchorWidth = anchorElement instanceof HTMLElement ? anchorElement.offsetWidth : 0;
    if (matchWidth && anchorWidth && panel.style.width !== `${anchorWidth}px`) {
      panel.style.width = `${anchorWidth}px`;
    }
    const gap = 4;
    const { offsetWidth: width, offsetHeight: height } = panel;
    // Nothing measurable (jsdom, a hidden tab) means no sane coordinates, so
    // daisyUI's own absolute positioning is left in place rather than replaced
    // with a guess.
    if (!width || !height || !window.innerWidth) {
      return;
    }
    // Capped and scrollable, then clamped on both axes. A panel taller than the
    // viewport used to be reachable by scrolling the page; fixed positioning
    // travels with the trigger, so anything past the edge is lost for good.
    const ceiling = Math.max(gap, minTop);
    const room = window.innerHeight - ceiling - gap;
    const capped = Math.min(height, room);
    const below = Math.max(anchor.bottom + gap, ceiling);
    const flip = below + capped > window.innerHeight && anchor.top - gap - capped > ceiling;
    const top = flip ? anchor.top - gap - capped : below;
    const raw = align === "end" ? anchor.right - width : anchor.left;
    // Written only where the value changes. The observer below watches the
    // element these writes land on, and re-writing an identical `maxHeight`
    // inside its own callback is what makes a browser report a resize loop.
    const set = (property: "position" | "margin" | "maxHeight" | "overflowY", value: string) => {
      if (panel.style[property] !== value) {
        panel.style[property] = value;
      }
    };
    set("position", "fixed");
    set("margin", "0");
    set("maxHeight", `${room}px`);
    set("overflowY", "auto");
    const wantTop = Math.max(ceiling, Math.min(top, window.innerHeight - capped - gap));
    const wantLeft = Math.max(gap, Math.min(raw, window.innerWidth - width - gap));
    const origin = fixedOrigin(panel);
    const nextTop = `${wantTop - origin.top}px`;
    const nextLeft = `${wantLeft - origin.left}px`;
    if (panel.style.top !== nextTop) {
      panel.style.top = nextTop;
    }
    if (panel.style.left !== nextLeft) {
      panel.style.left = nextLeft;
    }
  }, [align, anchorRef, detailsRef, matchWidth, minTop, open, panelRef]);

  useEffect(() => {
    const details = detailsRef ? detailsRef.current : null;
    const onToggle = (): void => place();
    details?.addEventListener("toggle", onToggle);
    // No `toggle` without a `details`: an anchored panel is placed when it
    // mounts, which is when `open` changes.
    if (!details) {
      place();
    }
    // A panel measured at open grows afterwards: a feed that opens on
    // "Loading…" and then fills. Clamped against the height it had then, a
    // grown panel runs past the bottom edge, and being fixed it cannot be
    // scrolled to.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => place());
    if (panelRef.current) {
      observer?.observe(panelRef.current);
    }
    // The anchor too: a list opening in flow can raise a scrollbar that
    // narrows the field it was sized to, and nothing else says so.
    let anchor: Element | null | undefined = null;
    if (details) {
      anchor = details.querySelector("summary");
    } else if (anchorRef) {
      anchor = anchorRef.current;
    }
    if (anchor) {
      observer?.observe(anchor);
    }
    // Capture, so a scroll in any ancestor moves the panel with its trigger
    // instead of leaving it stranded where it opened.
    document.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      observer?.disconnect();
      details?.removeEventListener("toggle", onToggle);
      document.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchorRef, detailsRef, panelRef, place]);

  return place;
}
